import { api } from './api';

export interface TransformationItem {
  id: number;
  title: string;
  description: string;
  category?: string;
  location?: string;
  beforeImage: string;
  afterImage: string;
  beforeThumbnail?: string;
  afterThumbnail?: string;
  createdAt: string;
  updatedAt?: string;
}

class TransformationService {
  private cache: TransformationItem[] | null = null;
  private cacheExpiry: number = 0;
  private cacheDuration = 5 * 60 * 1000; // 5 minutes

  async getTransformations(): Promise<TransformationItem[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cache && now < this.cacheExpiry) {
      return this.cache;
    }

    try {
      console.log('Fetching transformations from API...');
      const response = await api.getTransformations();
      const items = response.data;
      
      console.log('Raw transformations response:', items);
      console.log('Transformations count:', items.length);
      
      this.cache = items;
      this.cacheExpiry = now + this.cacheDuration;
      return items;
    } catch (error) {
      console.error('Error fetching transformations:', error);
      
      // Return fallback data if API fails
      console.log('Using fallback transformations data');
      return this.getFallbackData();
    }
  }

  async getTransformationById(id: number): Promise<TransformationItem | null> {
    const items = await this.getTransformations();
    return items.find(item => item.id === id) || null;
  }

  invalidateCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
  }

  private getFallbackData(): TransformationItem[] {
    return [
      {
        id: 1,
        title: "Nowoczesny ogród zimowy",
        description: "Przekształcenie nieużywanej przestrzeni w elegancki ogród zimowy z pełnym przeszkleniem.",
        category: "Ogród zimowy",
        location: "Piła",
        beforeImage: "/src/assets/Piła.jpeg",
        afterImage: "/src/assets/Piła2.jpeg",
        createdAt: new Date('2023-05-15').toISOString()
      },
      {
        id: 2,
        title: "Przeszklenie balkonu",
        description: "Eleganckie przeszklenie balkonu z nowoczesnym systemem bezramowym.",
        category: "Zabudowa balkonu",
        location: "Czarnkowo",
        beforeImage: "/src/assets/Czarnkowo.jpeg",
        afterImage: "/src/assets/Czarnkowo2.jpeg",
        createdAt: new Date('2023-06-01').toISOString()
      }
    ];
  }

  async addTransformation(item: any): Promise<TransformationItem> {
    try {
      const response = await api.addTransformation(item);
      this.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('Error adding transformation:', error);
      throw error;
    }
  }

  async updateTransformation(id: number, item: Partial<TransformationItem>): Promise<TransformationItem> {
    try {
      const response = await api.updateTransformation(id, item);
      this.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('Error updating transformation:', error);
      throw error;
    }
  }

  async deleteTransformation(id: number): Promise<void> {
    try {
      await api.deleteTransformation(id);
      this.invalidateCache();
    } catch (error) {
      console.error('Error deleting transformation:', error);
      throw error;
    }
  }

  async getTransformationStats(): Promise<any> {
    try {
      const response = await api.getTransformationStats();
      return response.data;
    } catch (error) {
      console.error('Error getting transformation stats:', error);
      throw error;
    }
  }
}

export const transformationService = new TransformationService();
export default transformationService;
